import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Pencil, Eye, ExternalLink, MessageSquare, ShoppingBag } from "lucide-react";
import { useEditMode } from "../context/EditModeContext";

const TESTIMONIAL_PAGES = new Set(["/", "/ateliers", "/cursus", "/stages"]);

/**
 * Barre flottante — visible uniquement pour les admins connectés.
 * Au-dessus navbar / banner / popup pour rester toujours cliquable.
 * Lien BO contextuel : produits / avis / dashboard.
 */
export default function EditModeBar() {
  const { canEdit, isEditMode, toggleEditMode } = useEditMode();
  const location = useLocation();

  if (!canEdit) return null;

  const onBookingTunnel = location.pathname.startsWith('/reserver');
  if (onBookingTunnel) return null;

  const isBoutique = location.pathname.startsWith("/boutique");
  const hasTestimonials = TESTIMONIAL_PAGES.has(location.pathname);
  const boHref = isBoutique
    ? "/admin/products"
    : hasTestimonials
      ? "/admin/testimonials"
      : "/admin";
  const boLabel = isBoutique
    ? "Gérer les produits"
    : hasTestimonials
      ? "Gérer les avis"
      : "Back-office";
  const BoIcon = isBoutique ? ShoppingBag : hasTestimonials ? MessageSquare : ExternalLink;

  return (
    <div className="edit-mode-bar fixed bottom-5 left-1/2 -translate-x-1/2 sm:left-6 sm:translate-x-0 z-[120] pointer-events-none">
      <div className="pointer-events-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <button
          type="button"
          onClick={toggleEditMode}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-md border backdrop-blur-md transition-all ${
            isEditMode
              ? "bg-white/95 border-indigo-200 text-slate-700 shadow-slate-200/80 hover:border-indigo-300 hover:bg-white"
              : "bg-[#3a00e0]/95 border-indigo-400/40 text-white shadow-[0_8px_24px_-6px_rgba(58,0,224,0.4)] hover:bg-[#5b21ff]"
          }`}
          aria-pressed={isEditMode}
          aria-label={
            isEditMode
              ? "Désactiver le mode édition"
              : "Activer le mode édition — cliquez pour voir les textes éditables"
          }
        >
          {isEditMode ? (
            <>
              <Eye className="h-3.5 w-3.5 shrink-0 text-indigo-500" aria-hidden="true" />
              <span className="flex flex-col items-start leading-tight text-left">
                <span className="text-[13px]">Mode édition actif</span>
                <span className="text-[10px] font-normal text-slate-500">
                  Textes marketing uniquement
                </span>
              </span>
              <span className="ml-0.5 rounded bg-indigo-50 text-indigo-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                ON
              </span>
            </>
          ) : (
            <>
              <Pencil className="h-3.5 w-3.5 shrink-0 text-[#fab03a]" aria-hidden="true" />
              <span className="flex flex-col items-start leading-tight text-left">
                <span className="text-[13px]">Activer l&apos;édition</span>
                <span className="text-[10px] font-normal text-white/80">
                  Affiche les textes modifiables
                </span>
              </span>
              <span className="ml-0.5 rounded bg-white/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">
                OFF
              </span>
            </>
          )}
        </button>

        <Link
          to={boHref}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium border backdrop-blur-md shadow-md transition-all bg-white/95 border-slate-200 text-slate-700 hover:border-[#3a00e0]/40 hover:text-[#3a00e0]"
        >
          <BoIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="text-[13px]">{boLabel}</span>
        </Link>
      </div>
    </div>
  );
}
